/**
 * Página de Aprovação de Médicos
 * Página dedicada para aprovar médicos no painel de admin
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Sidebar } from '@/components/common/Sidebar';
import { ThemeToggle } from '@/components/common';
import {
  getDefaultRouteByRole,
  isAdminRole,
  shouldFetchAdminResources,
} from '@/lib/roleRouting';
import {
  Search,
  Bell,
  Loader2,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  DoctorApprovalStats,
  SpecialtyFilter,
  DoctorApprovalTable,
  Pagination,
  DoctorDetailsModal,
} from '@/components/admin';
import {
  listDoctors,
  getPendingApprovalDoctors,
  getAdminDocumentViewUrl,
  approveDoctorAdmin,
} from '@/services';
import { toast } from 'sonner';
import { GlobalSearch, NotificationCenter } from '@/components/dashboard/HeaderActions';
import type { DoctorDTO, DoctorDocumentDTO } from '@/services';
import { DocumentViewerModal } from '@/components/documents/DocumentViewerModal';
import {
  countNewPendingApprovalDoctorsToday,
  countPendingApprovalDoctors,
  formatAverageApprovalReviewTime,
} from '@/lib/adminDoctorApprovalMetrics';

const ITEMS_PER_PAGE = 10;

export default function DoctorApprovalPage() {
  const navigate = useNavigate();
  const { user, isLoading, logout } = useAuth();
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [doctors, setDoctors] = useState<DoctorDTO[]>([]);
  const [allDoctors, setAllDoctors] = useState<DoctorDTO[]>([]);
  const [approvedCount, setApprovedCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorDTO | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [openRejectForm, setOpenRejectForm] = useState(false);

  // Document viewer
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<DoctorDocumentDTO | null>(null);
  const [viewingDoctorId, setViewingDoctorId] = useState<string | null>(null);

  // Get unique specialties from doctors
  const uniqueSpecialties = Array.from(
    new Set(doctors.map((d) => d.specialty).filter(Boolean))
  );

  // Filter doctors based on search and specialty
  const filteredDoctors = doctors.filter((doctor) => {
    const term = searchTerm.toLowerCase().trim();
    const crmLabel = `${doctor.crm || ''}/${doctor.crmState || ''}`.toLowerCase();
    const matchesSearch =
      !term ||
      doctor.user?.name?.toLowerCase().includes(term) ||
      doctor.user?.email?.toLowerCase().includes(term) ||
      crmLabel.includes(term) ||
      String(doctor.crm || '').toLowerCase().includes(term);

    const matchesSpecialty =
      !selectedSpecialty || doctor.specialty === selectedSpecialty;

    return matchesSearch && matchesSpecialty;
  });

  // Get pending doctors (not approved and not rejected)
  const pendingDoctors = filteredDoctors.filter(
    (d) => !d.isApproved && d.status !== 'REJECTED' && d.status !== 'APPROVED',
  );

  // Pagination
  const totalPages = Math.ceil(pendingDoctors.length / ITEMS_PER_PAGE);
  const paginatedDoctors = pendingDoctors.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Check auth
  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        navigate('/login');
        return;
      }
      if (!isAdminRole(user.role)) {
        navigate(getDefaultRouteByRole(user.role), { replace: true });
      }
    }
  }, [user, isLoading, navigate]);

  // Load data
  const loadData = async () => {
    setIsLoadingData(true);
    setError(null);

    try {
      // Usar getPendingApprovalDoctors() para médicos pendentes pois este endpoint
      // inclui o array `documents` no retorno — necessário para o contador correto.
      // listDoctors() é usado apenas para o count de aprovados no resumo.
      const [pendingResponse, allDoctorsResponse] = await Promise.all([
        getPendingApprovalDoctors(),
        listDoctors(),
      ]);

      setDoctors(Array.isArray(pendingResponse.data) ? pendingResponse.data : []);
      const allDoctorsData = Array.isArray(allDoctorsResponse.data) ? allDoctorsResponse.data : [];
      setAllDoctors(allDoctorsData);
      setApprovedCount(allDoctorsData.filter((d) => d.isApproved || d.status === 'APPROVED').length);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar dados de aprovação de médicos. Por favor, tente novamente.');
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    if (!shouldFetchAdminResources(user?.role, isLoading)) return;
    loadData();
  }, [user, isLoading]);

  // Handle approve doctor
  const handleApproveDoctor = async (doctorId: string) => {
    setIsApproving(true);
    try {
      // Use the admin-specific approval endpoint
      const result = await approveDoctorAdmin(doctorId);
      if (result.success) {
        toast.success('Médico aprovado com sucesso!');
        await loadData();
        setIsModalOpen(false);
        setCurrentPage(1);
      } else {
        toast.error(result.error || 'Erro ao aprovar médico');
      }
    } catch (err) {
      console.error('Erro ao aprovar médico:', err);
      toast.error('Erro ao conectar com o servidor');
    } finally {
      setIsApproving(false);
    }
  };

  // Handle open document viewer from admin context
  const handleViewDocument = (doctorId: string, doc: DoctorDocumentDTO) => {
    setViewingDoctorId(doctorId);
    setViewingDoc(doc);
    setViewerOpen(true);
  };

  // Handle view doctor details
  const handleViewDoctor = (doctorId: string, options?: { openReject?: boolean }) => {
    const doctor = doctors.find((d) => d.id === doctorId);
    if (doctor) {
      setSelectedDoctor(doctor);
      setOpenRejectForm(!!options?.openReject);
      setIsModalOpen(true);
    }
  };

  const handleModalDecisionComplete = async () => {
    await loadData();
    setIsModalOpen(false);
    setOpenRejectForm(false);
    setCurrentPage(1);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!isAdminRole(user.role)) {
    return null;
  }

  const pendingCount = countPendingApprovalDoctors(doctors);
  const newTodayCount = countNewPendingApprovalDoctorsToday(allDoctors);
  const avgReviewTime = formatAverageApprovalReviewTime(allDoctors);

  const specialtyList = ['Todas as Especialidades', ...uniqueSpecialties];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex">
      {/* Sidebar */}
      <Sidebar
        userRole={user.role}
        userName={user.name}
        onLogout={handleLogout}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-8 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Aprovação de Médicos
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Revise e aprove aplicações de novos médicos
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="relative hidden md:block w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar aplicações..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10 border-gray-300 dark:border-neutral-700 dark:bg-neutral-800 dark:text-white"
              />
            </div>

            <GlobalSearch />
            <NotificationCenter />
            <ThemeToggle />
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-8 bg-gray-50 dark:bg-neutral-950">
          {/* Error Alert */}
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* Success Summary */}
          <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800 dark:text-green-200">
              {approvedCount} de {approvedCount + doctors.length} médicos aprovados
            </p>
          </div>

          {/* Important Info */}
          <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                ⚠️ Requisito de Documentos Profissionais
              </p>
              <p className="text-blue-800 dark:text-blue-200">
                Para aprovar um médico no fluxo atual, revise dados cadastrais, CRM, documentos
                profissionais e o vídeo de confirmação (~3s). Cadastros legados sem vídeo
                continuam analisáveis pelo documento antigo. O botão de aprovação fica
                desabilitado até os requisitos mínimos estarem presentes.
              </p>
            </div>
          </div>

          {/* Stats Cards */}
          {!isLoadingData && (
            <DoctorApprovalStats
              totalPending={pendingCount}
              newToday={newTodayCount}
              avgReviewTime={avgReviewTime}
            />
          )}

          {/* Specialty Filter */}
          {!isLoadingData && (
            <SpecialtyFilter
              selectedSpecialty={selectedSpecialty}
              onSpecialtyChange={(specialty) => {
                setSelectedSpecialty(specialty);
                setCurrentPage(1);
              }}
              availableSpecialties={specialtyList}
            />
          )}

          {/* Loading state */}
          {isLoadingData ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-teal-600 animate-spin mr-2" />
              <p className="text-gray-600 dark:text-gray-400">Carregando aplicações...</p>
            </div>
          ) : (
            <>
              {/* Table */}
              <DoctorApprovalTable
                doctors={paginatedDoctors}
                isLoading={isLoadingData}
                onApprove={handleApproveDoctor}
                onReject={(doctorId) => handleViewDoctor(doctorId, { openReject: true })}
                onView={(doctorId) => handleViewDoctor(doctorId)}
              />

              {/* Pagination */}
              {pendingDoctors.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={pendingDoctors.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setCurrentPage}
                />
              )}

              {/* Empty State */}
              {pendingDoctors.length === 0 && !isLoadingData && (
                <div className="bg-white dark:bg-neutral-900 rounded-lg border border-gray-200 dark:border-neutral-800 p-12 text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Tudo em dia!
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Nenhuma aprovação de médico pendente neste momento.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Doctor Details Modal */}
          <DoctorDetailsModal
            doctor={selectedDoctor}
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setOpenRejectForm(false);
            }}
            onApprove={handleModalDecisionComplete}
            onRejected={handleModalDecisionComplete}
            isApproving={isApproving}
            initialShowRejectionForm={openRejectForm}
            onViewDocument={(doc) => {
              if (selectedDoctor?.id) {
                handleViewDocument(selectedDoctor.id, doc);
              }
            }}
          />

          {/* Document Viewer Modal (admin context) */}
          <DocumentViewerModal
            isOpen={viewerOpen}
            onClose={() => { setViewerOpen(false); setViewingDoc(null); setViewingDoctorId(null); }}
            document={viewingDoc}
            userId={viewingDoctorId ?? ''}
            fetchViewUrl={getAdminDocumentViewUrl}
          />
        </main>
      </div>
    </div>
  );
}
